import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '@/hooks/use-auth';
import type { BrandKit, BrandKitFormData } from '@/types/brand-kit';
import { logger } from '@/utils/logger';
import { showErrorToast, showSuccessToast } from '@/utils/toast';

// F-082: lazy-load BrandKitService so its heavy transitive dep chain
// (browser-image-compression ~280KB) stays OUT of the root entry graph.
// useBrandKit is consumed by shell-adjacent surfaces, so a static import here
// re-introduced the very chain BrandKitContext already deferred. The service
// is only ever touched inside async callbacks, so a dynamic import is free.
const getBrandKitService = () =>
  import('@/services/data/BrandKitService').then((m) => m.brandKitService);

export interface UseBrandKitReturn {
  brandKit: BrandKit | null;
  isLoading: boolean;
  error: string | null;
  updateBrandKit: (
    data: Partial<BrandKitFormData>,
    options?: { silent?: boolean },
  ) => Promise<void>;
  uploadLogo: (file: File, variant: 'primary' | 'light' | 'dark') => Promise<void>;
  deleteLogo: (variant: 'primary' | 'light' | 'dark') => Promise<void>;
  refreshBrandKit: () => Promise<void>;
}

function isTransientAuthError(message: string): boolean {
  return (
    message.includes('Failed to fetch') ||
    message.includes('Lock broken') ||
    message.includes('steal') ||
    message.includes('navigator.locks') ||
    message.includes('AbortError') ||
    // Auth hydration race on cold page load — the hook fires before
    // supabase.auth.getUser() has settled. Suppress the toast; the
    // effect will re-run when userId populates and retry cleanly.
    message.includes('User not authenticated') ||
    message.includes('not authenticated')
  );
}

export function useBrandKit(kitId?: string | null): UseBrandKitReturn {
  const { user } = useAuth();
  const userId = user?.id;
  const [brandKit, setBrandKit] = useState<BrandKit | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasInitializedRef = useRef(false);
  const loadingRef = useRef(false);

  const loadBrandKit = useCallback(async () => {
    if (!userId || loadingRef.current) {
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const brandKitService = await getBrandKitService();
      // BI-milestone post-ship audit fix #4: pass userId for an explicit
      // ownership-scoped lookup — a post's own brand_kit_id must never
      // resolve to a kit belonging to a different account.
      const kit = kitId
        ? await brandKitService.getBrandKitById(kitId, userId)
        : await brandKitService.getOrCreateBrandKit();
      if (kitId && !kit) {
        setError('Brand kit not found');
      } else {
        setBrandKit(kit);
      }
      hasInitializedRef.current = true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load brand kit';

      if (isTransientAuthError(errorMessage)) {
        logger.debug('Brand kit load: transient auth error suppressed', errorMessage);
      } else {
        setError(errorMessage);
        if (hasInitializedRef.current) {
          showErrorToast('Error loading brand kit', errorMessage);
        }
      }
    } finally {
      setIsLoading(false);
      hasInitializedRef.current = true;
      loadingRef.current = false;
    }
  }, [userId, kitId]);

  const updateBrandKit = useCallback(
    async (data: Partial<BrandKitFormData>, options?: { silent?: boolean }) => {
      try {
        const brandKitService = await getBrandKitService();
        const updated = await brandKitService.updateBrandKit(data, kitId ?? undefined);
        setBrandKit(updated);
        if (!options?.silent) {
          showSuccessToast('Brand kit updated', 'Your brand settings have been saved.');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update brand kit';
        showErrorToast('Error updating brand kit', errorMessage);
        throw err;
      }
    },
    [kitId],
  );

  const uploadLogo = useCallback(
    async (file: File, variant: 'primary' | 'light' | 'dark') => {
      try {
        const brandKitService = await getBrandKitService();
        await brandKitService.uploadLogo(file, variant, kitId ?? undefined);
        await loadBrandKit();
        showSuccessToast('Logo uploaded', 'Your logo has been uploaded successfully.');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to upload logo';
        showErrorToast('Error uploading logo', errorMessage);
        throw err;
      }
    },
    [loadBrandKit, kitId],
  );

  const deleteLogo = useCallback(
    async (variant: 'primary' | 'light' | 'dark') => {
      try {
        const brandKitService = await getBrandKitService();
        await brandKitService.deleteLogo(variant, kitId ?? undefined);
        await loadBrandKit();
        showSuccessToast('Logo removed', 'Your logo has been removed.');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete logo';
        showErrorToast('Error removing logo', errorMessage);
        throw err;
      }
    },
    [loadBrandKit, kitId],
  );

  useEffect(() => {
    if (userId) {
      loadBrandKit();
    } else {
      setBrandKit(null);
      setIsLoading(false);
      setError(null);
    }
  }, [userId, kitId, loadBrandKit]);

  return {
    brandKit,
    isLoading,
    error,
    updateBrandKit,
    uploadLogo,
    deleteLogo,
    refreshBrandKit: loadBrandKit,
  };
}
